import type {CodeSection, CodeSectionLookup} from '@github-ui/code-nav'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {SafeHTMLString} from '@github-ui/safe-html'
import {useCodeViewOptions} from '@github-ui/use-code-view-options'
import {clsx} from 'clsx'
import React, {useEffect, useImperativeHandle, useRef, useState} from 'react'

import {useDeferredAST} from '../../../../contexts/DeferredASTContext'
import {useIsCursorEnabled} from '../../../../hooks/use-cursor-navigation'
import type {SharedLineObserverHandle} from '../../../../hooks/use-shared-line-observer'
import type {SetStickyLinesType} from '../../../../hooks/use-sticky-lines'
import {isLineInViewport, queryMatchElement, useShouldUseInert} from '../../../../utilities/lines'
import SelectAllShortcut from '../../../../utilities/SelectAllShortcut'
import type {CodeNavData} from '../BlobContent'
import type {CodeLinesHandle} from './code-lines-handle'
import {CodeFoldingEllipsisOverlay} from './CodeFoldingEllipsisOverlay'
import {CodeLine} from './CodeLine'
import codeLinesStyles from './CodeLines.module.css'
import type {CodeLineData} from './hooks/use-code-lines'
import {useVirtualCodeBlob} from './hooks/use-virtual-code-blob'
import {LineNumber} from './LineNumber'

export interface CodeLinesProps {
  linesData: CodeLineData[]
  tabSize: number
  onCollapseToggle: () => void
  nonTruncatedLinesData?: CodeLineData[]
  materializeAllLines?: boolean
  colorizedLines?: SafeHTMLString[] | null
  textOverlayShouldBeVisible?: boolean
  codeSections?: Map<number, CodeSection[]>
  codeLineToSectionMap?: CodeSectionLookup
  contentWidth?: number
  textAreaRef?: React.RefObject<HTMLTextAreaElement | null>
  cursorContainerRef?: React.RefObject<HTMLDivElement | null>
  isTextAreaFocused?: boolean
  textSelection?: {start: number; end: number; keyboard: boolean; displayStart: boolean}
  additionalTextAreaInstructions?: string
  copilotAccessAllowed: boolean
  onLineNumberClick?: React.MouseEventHandler<HTMLDivElement>
  onLineStickOrUnstick?: SetStickyLinesType
  sharedLineObserver?: SharedLineObserverHandle
  onCodeNavTokenSelected?: (symbol: CodeNavData) => void
  ref?: React.Ref<CodeLinesHandle>
}

export const CodeLines = React.memo(CodeLinesUnmemoized)

function CodeLinesUnmemoized({
  linesData,
  onLineNumberClick,
  codeSections,
  codeLineToSectionMap,
  onLineStickOrUnstick,
  sharedLineObserver,
  tabSize,
  contentWidth,
  onCollapseToggle,
  cursorContainerRef,
  textAreaRef,
  materializeAllLines,
  copilotAccessAllowed,
  ref,
}: CodeLinesProps) {
  // This component drives a window virtualizer (@tanstack/react-virtual), whose imperative measurements the React
  // Compiler cannot reason about. Opt out of compilation so the virtualizer measures every render as it expects.
  'use no memo'

  const parentRef = useRef<HTMLTableElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const horizontalScrollBarRef = useRef<HTMLDivElement>(null)
  const shouldUseCursor = useIsCursorEnabled()
  // Prevent loops and jumping when scrolling
  const setNextTextAreaScroll = useRef(true)
  const setNextScrollBarScroll = useRef(true)
  const setNextScrollContainerScroll = useRef(true)
  const {stylingDirectives} = useDeferredAST()

  // Track the scroll container's width in state so we can decide whether to render the custom horizontal scrollbar
  // without reading a DOM ref during render. Only observed in cursor mode, since that's the only mode that renders
  // the custom scrollbar. The observer callback is rAF-coalesced and bails when the width is unchanged so a
  // drag-resize doesn't re-render this (compiler-opted-out) component on every tick.
  const [scrollContainerWidth, setScrollContainerWidth] = useState<number>()
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current
    if (!shouldUseCursor || !scrollContainer) return

    let frame = 0
    const observer = new ResizeObserver(entries => {
      const entry = entries[0]
      if (!entry) return
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        const nextWidth = entry.target.clientWidth
        setScrollContainerWidth(prev => (prev === nextWidth ? prev : nextWidth))
      })
    })
    observer.observe(scrollContainer)

    return () => {
      cancelAnimationFrame(frame)
      observer.disconnect()
    }
  }, [shouldUseCursor])

  useEffect(() => {
    //need to set the on scroll here because it relies on having access to parentRef to sync up the scrolls
    if (textAreaRef && textAreaRef.current) {
      textAreaRef.current.onscroll = () => {
        if (scrollContainerRef.current && textAreaRef?.current) {
          if (!setNextScrollContainerScroll.current) {
            setNextScrollContainerScroll.current = true
            return
          }

          if (textAreaRef.current.scrollLeft === scrollContainerRef.current.scrollLeft) return

          // Goes in a loop textArea -> contianer -> scrollBar -> textArea , so we need to prevent the next scroll on the textArea
          setNextTextAreaScroll.current = !(setNextScrollBarScroll.current && setNextScrollContainerScroll.current)
          scrollContainerRef.current.scrollLeft = textAreaRef.current.scrollLeft
        }
        if (cursorContainerRef && cursorContainerRef.current && textAreaRef?.current) {
          cursorContainerRef.current.scrollLeft = textAreaRef.current.scrollLeft
        }
      }
      const textRef = textAreaRef.current
      return () => {
        if (textRef) {
          textRef.onscroll = null
        }
      }
    }
  }, [textAreaRef, parentRef, shouldUseCursor, cursorContainerRef])

  const wrapOptionEnabled = useCodeViewOptions().codeWrappingOption.enabled
  const shouldUseInert = useShouldUseInert()
  const isolateCodeLines = isFeatureEnabled('react_blob_isolate_code_lines')

  const {virtualizer, currentLineHeight} = useVirtualCodeBlob({
    parentRef,
    lineCount: linesData.length,
    materializeAllLines: !!materializeAllLines,
  })

  // When wrap option changes, recalculates virtualizer heights
  useEffect(() => {
    virtualizer.measure()
  }, [wrapOptionEnabled, virtualizer])

  useImperativeHandle(ref, () => ({
    scrollToTop: () => {
      if (isLineInViewport(0)) return
      virtualizer.scrollToIndex(0, {align: 'start'})
    },
    scrollToLine: (lineNumber, column) => {
      //align: start because this positions the element closer to the top of the screen
      virtualizer.scrollToIndex(lineNumber, {align: 'start'})

      const container = parentRef.current
      if (!container) {
        return
      }

      container.scroll({left: getHorizontalScrollOffset(container, lineNumber, column)})
    },
  }))

  return (
    <div
      ref={parentRef}
      style={{pointerEvents: shouldUseCursor ? 'none' : 'auto'}}
      onScroll={event => handleBlobScrollSync(event, textAreaRef)}
    >
      <div
        className={clsx(
          codeLinesStyles.reactCodeLineContainer,
          shouldUseCursor ? codeLinesStyles.scrollContainerHidden : codeLinesStyles.scrollContainerAuto,
        )}
        ref={scrollContainerRef}
        //TODO: this is necessary to resolve an axe complaint, but we don't actually want this to be in the tab order.
        //unsure what the resolution to that issue is outside of an onFocus
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- pre-existing for axe compliance
        tabIndex={0}
        onScroll={() => {
          if (shouldUseCursor && scrollContainerRef.current && horizontalScrollBarRef.current) {
            if (!setNextScrollBarScroll.current) {
              setNextScrollBarScroll.current = true
              return
            }

            if (horizontalScrollBarRef.current.scrollLeft === scrollContainerRef.current.scrollLeft) return
            // Goes in a loop container -> scrollBar -> textArea -> container, so we need to prevent the next scroll on the container
            setNextScrollContainerScroll.current = !(setNextScrollBarScroll.current && setNextTextAreaScroll.current)
            horizontalScrollBarRef.current.scrollLeft = scrollContainerRef.current.scrollLeft
          }
        }}
      >
        <div
          /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
          className={clsx('react-code-file-contents', codeLinesStyles.codeFileContents)}
          data-isolate-layers={isolateCodeLines || undefined}
          role="presentation"
          aria-hidden
          data-tab-size={tabSize}
          data-testid={'code-lines-container'}
          data-paste-markdown-skip
          style={{
            tabSize,
            width: contentWidth,
            maxWidth: wrapOptionEnabled ? '100%' : 'unset',
            height: virtualizer.getTotalSize(),
          }}
          data-hpc
        >
          <div
            /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
            className="react-line-numbers"
            style={{
              pointerEvents: 'auto',
              height: virtualizer.getTotalSize(),
              position: 'relative',
              zIndex: 2,
            }}
          >
            {virtualizer.getVirtualItems().map(virtualItem => {
              const lineData = linesData[virtualItem.index]!
              return (
                <LineNumber
                  codeLineData={lineData}
                  key={lineData.lineNumber}
                  onClick={onLineNumberClick}
                  ownedCodeSections={codeSections}
                  onLineStickOrUnstick={onLineStickOrUnstick}
                  onCollapseToggle={onCollapseToggle}
                  virtualOffset={virtualItem.start}
                  copilotAccessAllowed={copilotAccessAllowed}
                />
              )
            })}
          </div>
          {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
          <div className="react-code-lines" style={{height: virtualizer.getTotalSize()}}>
            {virtualizer.getVirtualItems().map(virtualItem => {
              const lineData = linesData[virtualItem.index]!
              return (
                <CodeLine
                  codeLineData={lineData}
                  stylingDirectivesLine={
                    lineData.stylingDirectivesLine ??
                    (stylingDirectives ? stylingDirectives[lineData.lineNumber - 1] : undefined)
                  }
                  shouldUseInert={shouldUseInert}
                  codeLineClassName={lineData.codeLineClassName}
                  key={lineData.lineNumber}
                  id={`LC${lineData.lineNumber}`}
                  onLineStickOrUnstick={onLineStickOrUnstick}
                  sharedLineObserver={sharedLineObserver}
                  setIsCollapsed={onCollapseToggle}
                  codeLineToSectionMap={codeLineToSectionMap}
                  virtualOffset={virtualItem.start}
                  virtualKey={String(virtualItem.key)}
                  copilotAccessAllowed={copilotAccessAllowed}
                  currentLineHeight={currentLineHeight}
                  measureRef={wrapOptionEnabled ? virtualizer.measureElement : undefined}
                  virtualIndex={wrapOptionEnabled ? virtualItem.index : undefined}
                />
              )
            })}
          </div>
          <SelectAllShortcut
            shouldNotOverrideCopy={shouldUseCursor}
            containerRef={shouldUseCursor ? textAreaRef : parentRef}
          />
          {!wrapOptionEnabled && (
            <CodeFoldingEllipsisOverlay
              linesData={linesData}
              onLineStickOrUnstick={onLineStickOrUnstick}
              setIsCollapsed={onCollapseToggle}
              tabSize={tabSize}
              extraLeftPadding={82}
            />
          )}
        </div>
      </div>
      {shouldUseCursor && contentWidth && scrollContainerWidth !== undefined && scrollContainerWidth < contentWidth ? (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- scrollbar container, pre-existing
        <div
          className={codeLinesStyles.scrollBarContainer}
          onScroll={() => {
            if (horizontalScrollBarRef.current && textAreaRef?.current) {
              if (!setNextTextAreaScroll.current) {
                setNextTextAreaScroll.current = true
                return
              }

              if (horizontalScrollBarRef.current.scrollLeft === textAreaRef.current.scrollLeft) return

              // Goes in a loop scrollBar -> textArea -> container -> scrollBar, so we need to prevent the next scroll on the scrollBar
              setNextScrollBarScroll.current = !(setNextScrollContainerScroll.current && setNextTextAreaScroll.current)
              textAreaRef.current.scrollLeft = horizontalScrollBarRef.current.scrollLeft
            }
          }}
          ref={horizontalScrollBarRef}
          // Prevent cursor events

          onClick={ev => ev.preventDefault()}
          onMouseDown={ev => ev.preventDefault()}
          onMouseUp={ev => ev.preventDefault()}
        >
          <div className={codeLinesStyles.scrollBarSpacer} style={{width: contentWidth}} />
        </div>
      ) : null}
    </div>
  )
}

// Because we are relying on the text area's highlighting to show the highlights, we need
// to sync the horizontal scrolling of the visible blob and the invisible text area
export function handleBlobScrollSync(
  event: React.UIEvent<HTMLDivElement>,
  textAreaRef: React.RefObject<HTMLTextAreaElement | null> | undefined,
) {
  const scrolledElement = event.target as HTMLElement
  textAreaRef?.current?.scrollTo(scrolledElement.scrollLeft, scrolledElement.scrollTop)
}

export function getHorizontalScrollOffset(container: HTMLElement, lineNumber: number, column?: number): number {
  if (!column) {
    return 0
  }
  const element = queryMatchElement(lineNumber, column)
  if (!element) {
    return 0
  }

  const containerRect = container.getBoundingClientRect()
  const elementRect = element.getBoundingClientRect()
  const fitsWithoutScroll =
    containerRect.left + containerRect.width - container.scrollLeft - (elementRect.left + elementRect.width) > 0

  // Prefer 0 offset if element fits to avoid annoying micro scrolling
  return fitsWithoutScroll ? 0 : element.offsetLeft
}
